import { cva } from 'class-variance-authority';

import { focusRing } from '../styles/focusRing';

/**
 * Tag class recipes.
 *
 * Ported from the reworked Figma component set (file 5aSkE0U4TeWbuIZDvccHjc, node 597:43779),
 * whose 24 cells are State (Default / Hover / Focus) x Type (Neutral / Accent / Success /
 * Destructive) x Size (md / Sm). Every geometry value below is read off that set.
 *
 * The `Colour` variant has SIX members, not the four the file draws: `warning` and `highlight` are
 * extensions, added for the surfaces that use the legacy `StatusIndicator`'s orange and purple.
 * They are the two values here with no cell to check against — see `.ds-tag-warning` and
 * `.ds-tag-highlight` in `design-system.css`.
 *
 * Like `Button.recipe`, this contains ZERO `dark:` variants: colour comes entirely from the
 * `--ds-tag-*` slots, which the `.ds-tag` / `.ds-tag-<colour>` classes in
 * `packages/design-system/src/styles/design-system.css` set. Those now read the DS 2.0 SEMANTIC roles rather than
 * the legacy ramp, so the slot layer no longer restates each cell per theme either.
 *
 * Sizing is height-based rather than padding-based, for the same reason `Button` is. Figma strokes
 * these INSIDE the shape, so the authored 32px cell with `py: 6` and a 20px line box only adds up
 * if the 1px border is painted within the height. A fixed height plus the app's global
 * `box-sizing: border-box` (`src/index.css`) reproduces that exactly; `py-[6px]` plus a border
 * would measure 34px. The `sm` cell says the same thing with different numbers — `py: 2` around
 * the same 20px line box, inside a 24px height.
 *
 *                height   pl    pr    gap   icon   remove box   glyph
 *   med            32     12    10     4     16        16        12
 *   sm             24      6     4     4     16        16        12
 *
 * `sm` GREW FROM 20 TO 24 on 2026-09-17, and it is the only geometry the re-check moved. The file
 * now authors `pad=2/4/2/6` on a HUG-height box rather than `0/4/0/6` on a 20px one, so the
 * horizontal padding is unchanged and the vertical is what grew. Two things get better with it:
 * the 16px remove control gains 4px of clearance instead of 2, and the 24px touch target
 * `tagRemoveRecipe` draws now fits the tag's own height exactly rather than overflowing it.
 *
 * THREE NUMBERS CHANGED IN THE REWORK, and all three used to be justified in this file as design
 * feedback on HNS-334 that superseded the old cells. The reworked set supersedes that feedback in
 * turn, so they now come straight off the file again:
 *
 *   - Horizontal padding is no longer a `size x deletable` compound. The file binds the root's
 *     padding as a property of the SIZE variant alone (`x3` / `x2,5` at md, `x1,5` / `x1` at sm),
 *     identical whether `isRemovable` is on or off — so the `deletable` variant that existed only
 *     to carry an asymmetric right padding is gone.
 *   - The leading icon is 16px at BOTH sizes, not 16 / 14. The `Waveform` instance measures
 *     16x16 in all 24 cells.
 *   - The remove control is a 16px box at BOTH sizes, not 20 / 16. It is a `Button` instance at
 *     `Size=Tiny, Type=Muted, iconOnly=Yes` in every cell, 16x16 with 2px padding around a 12px
 *     glyph. The glyph was already 12px in both and is unchanged.
 *
 * The gap between the label and the remove control is **4px at both sizes**, and the file now
 * spends it the same way at both: 2px of `padding-right` on the label's wrapper plus 2px on the
 * remove control's side (`Icon Container` at md, the `Text Container` gap at Sm). Code carries
 * the first as the label's `pr-0.5` and the second as the control's `ml-0.5`, so the split
 * survives `isRemovable=false` exactly as the file's does.
 *
 * This was briefly 6px, on a verbal request for "2px more than the authored 4". The subsequent
 * Figma edit redistributed the 4 as 2 + 2 rather than adding to it — measured after the edit,
 * both sizes are still 95 and 83 wide — so the file is the authority again and the extra 2px is
 * gone.
 *
 * The authored radius is `radius/full`, which clamps to fully-rounded at both heights, so
 * `rounded-full` is not an approximation.
 */
export const tagRecipe = cva(
  [
    'group/tag ds-tag',
    // `max-w-full` + the `min-w-0` chain below is what makes a long label truncate instead of
    // pushing the remove control out of the tag.
    // NO `gap` on the ROOT, deliberately. Both joins are 4px but they are BUILT differently —
    // the icon's is one gap, the label's is 2px of its own padding plus 2px on the remove
    // control — so a single root gap would double one of them. The icon's 4px is
    // `tagContentRecipe`'s own `gap-1` (icon and label are siblings inside it); the label's is
    // split the way the file splits it.
    'inline-flex max-w-full items-center',
    'rounded-full border border-solid',
    'font-sans text-label-md',
    'transition-colors duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',

    // Slot reads. The `.ds-tag-*` classes supply the values. The leading icon keeps its own
    // `--ds-tag-icon-fg` slot even though it resolves to the same role as the label at rest,
    // because the two separate on hover — see `tagIconRecipe`.
    'bg-[var(--ds-tag-bg)] border-[var(--ds-tag-border)] text-[var(--ds-tag-fg)]',

    // Disabled. `aria-disabled` rather than a `data-` attribute because the root is a plain
    // container, not a control — and because axe-core's colour-contrast rule skips text inside an
    // `aria-disabled` subtree, which is what keeps the disabled cells (contrast-exempt under WCAG
    // 1.4.3 as inactive) from failing the a11y gate that every enabled cell now passes.
    //
    // The reworked Figma set draws NO disabled cell — its State axis is Default / Hover / Focus.
    // These four lines are therefore derived from the role layer's own `-disabled` family rather
    // than read off the file; see the note in `design-system.css`.
    'aria-disabled:bg-[var(--ds-tag-bg-disabled)]',
    'aria-disabled:border-[var(--ds-tag-border-disabled)]',
    'aria-disabled:text-[var(--ds-tag-fg-disabled)]',
    'aria-disabled:[--ds-tag-icon-fg:var(--ds-tag-icon-fg-disabled)]',
  ],
  {
    variants: {
      /**
       * The axis is HEIGHT, and now padding too. Figma's values are `md` and `Sm`; the prop keeps
       * `med` / `sm` so the 9 existing call sites are untouched, and because `med` is what
       * `Tag`'s own API has always said.
       */
      size: {
        med: 'h-8 pr-2.5 pl-3',
        sm: 'h-6 pr-1 pl-1.5',
      },
      color: {
        neutral: 'ds-tag-neutral',
        primary: 'ds-tag-primary',
        success: 'ds-tag-success',
        destructive: 'ds-tag-destructive',
        // Not a Figma Type. See the `.ds-tag-warning` note in `design-system.css`.
        warning: 'ds-tag-warning',
        // Not a Figma Type either. See `.ds-tag-highlight` in `design-system.css`.
        highlight: 'ds-tag-highlight',
      },
      /**
       * Figma's `Hover` state.
       *
       * The set draws hover on every cell, including the ones with nothing to click. Applying it
       * only when the tag actually has a control is deliberate: a plain tag is a piece of data,
       * and a colour change under the pointer on something inert reads as an affordance that is
       * not there. The 8 non-interactive `Hover` cells are therefore unreachable in code —
       * flagged to design rather than silently dropped.
       *
       * THE LABEL DOES NOT MOVE. All 8 hovered cells keep their resting `foreground/<role>`;
       * only the fill, the border and the leading icon step. That is why there is no
       * `--ds-tag-fg-hover` read here any more.
       *
       * The `:not([aria-disabled=true])` guard makes these selectors disjoint from the disabled
       * ones above, so which rule wins never depends on utility sort order.
       */
      interactive: {
        true: [
          '[&:not([aria-disabled=true]):hover]:bg-[var(--ds-tag-bg-hover)]',
          '[&:not([aria-disabled=true]):hover]:border-[var(--ds-tag-border-hover)]',
          '[&:not([aria-disabled=true]):hover]:[--ds-tag-icon-fg:var(--ds-tag-icon-fg-hover)]',
        ],
        false: '',
      },
    },
    defaultVariants: { color: 'neutral', size: 'med', interactive: false },
  }
);

/**
 * Undoes the user-agent's own button chrome.
 *
 * REQUIRED, not defensive. This layer deliberately omits Tailwind's preflight (see
 * `design-system.css`), so nothing else resets a native `<button>` — and `tagContentRecipe` below
 * dresses one. (The remove control no longer needs it: `IconButton` sets its own background,
 * border width and font on every variant, which is how `Button` escapes the same trap.) Without this, Chromium paints each one with `background: buttonface`
 * (rgb(239,239,239)), a `2px outset` black border and `1px 6px` of padding, and — because a button
 * does not inherit typography — renders its label in **Arial 13.33px/400** instead of the tag's
 * Google Sans Flex 12px/500. Measured in Chromium; every one of those numbers was observed, not
 * anticipated.
 *
 * `font: inherit` rather than a family/size/weight trio: it is the one declaration that covers the
 * whole inherited font, including line-height, so the label inside a clickable tag is typeset by
 * the ROOT and cannot drift from a plain tag's.
 *
 * `Button` in this same layer escapes the trap only by setting its background, border width and
 * font explicitly on every variant. A control that leaves any of them unset needs this list.
 */
const UA_BUTTON_RESET = ['m-0 appearance-none border-0 bg-transparent p-0', '[font:inherit]'];

/**
 * The icon + label group.
 *
 * A button when `onClick` is passed and a plain `span` otherwise, which is why the focus treatment
 * lives here rather than on the root: it must be reachable when this is a control and absent when
 * it is not. `rounded-full` matches the tag's own shape so the focus ring traces the tag rather
 * than a rectangle inside it.
 *
 * BEYOND THE FILE. Figma's `Focus` state applies to the remove control only — the set has no
 * concept of a clickable label, so there is no cell to copy. The shared ring is kept for WCAG
 * 2.4.7. Raised with design: a clickable tag needs a focus cell of its own.
 */
export const tagContentRecipe = cva(
  [
    ...UA_BUTTON_RESET,
    // The label takes the tag's colour; the leading icon and the remove glyph each have their own.
    'text-[color:inherit]',
    'inline-flex min-w-0 items-center gap-1 rounded-full',
    'text-left',
    focusRing(),
  ],
  {
    variants: {
      interactive: {
        true: [
          'cursor-pointer [&[data-disabled]]:cursor-not-allowed',
          // Phones: the same invisible 44px target `Button.recipe` draws. Layout and paint unchanged.
          "relative max-sm:before:absolute max-sm:before:top-1/2 max-sm:before:left-1/2 max-sm:before:h-11 max-sm:before:w-[max(100%,--spacing(11))] max-sm:before:-translate-1/2 max-sm:before:content-['']",
        ],
        false: '',
      },
    },
    defaultVariants: { interactive: false },
  }
);

/**
 * The label itself.
 *
 * `min-w-0` is required in addition to the wrapper's: `truncate` can only shrink a flex item whose
 * minimum content size has been released, and there are two flex levels between here and the root.
 */
export const tagLabelRecipe = cva([
  'min-w-0 truncate',
  // The file's `Text Container` padding-right. Paired with the remove control's own `ml-0.5`
  // (its `Icon Container`), the two make the 4px the file measures between the label and the ×.
  //
  // PADDING ON THE LABEL rather than a flex gap, because that is how the file models it — a
  // wrapper carrying `padding-right`, which survives `isRemovable=false`. A plain md tag is
  // therefore 12 left / 12 right, matching the file.
  'pr-0.5',
]);

/**
 * Sizes and colours the `icon` slot. 16px at BOTH sizes — the file's `Waveform` instance measures
 * 16x16 in all 24 cells, so this is no longer a size-dependent value.
 *
 * The colour comes from `--ds-tag-icon-fg` rather than being inherited, and the slot survives
 * even though it now resolves to the label's own `foreground/<role>` at rest on the three
 * coloured Types. Two reasons it is not collapsed into an inherit: the icon steps to `/hover`
 * while the label holds, and Neutral colours the two differently at rest
 * (`foreground/secondary` under a `foreground/primary` label). Setting `text` here means a
 * caller's glyph still just uses `currentColor` and needs to know nothing about it.
 */
export const tagIconRecipe = cva([
  'inline-flex size-4 shrink-0 items-center justify-center',
  'text-[var(--ds-tag-icon-fg)]',
  'transition-colors duration-[var(--motion-control)] ease-[var(--motion-control-ease)] motion-reduce:transition-none',
  '[&_svg]:size-full',
]);

/**
 * What the Tag adds ON TOP of the real `IconButton` for its remove control.
 *
 * The control itself is `IconButton` at `size="3xs" appearance="tertiary" fill="default"`, which is
 * the file's `Size=Tiny, Type=Muted, Fill=Default, iconOnly=Yes` instance — 16px box, 12px glyph,
 * 2px padding, fully round. Going through the real component rather than a hand-rolled button is what
 * gives it HOVER and PRESSED states: `background/on-surface/hover` and `/active`, the translucent
 * overlays the Button's `muted-default` slots already name. An earlier version of this file drew the
 * × itself and asserted the file had no hover cell for it; that was wrong — it read the Tag set's
 * pinned `State=Default` instance rather than the Button component behind it, which carries all
 * four states.
 *
 * `muted-default` also supplies the right colours for free, so there is no `--ds-tag-remove-fg`
 * any more: its `--ds-btn-fg` is `foreground/primary` and its `--ds-btn-fg-disabled` is
 * `foreground/disabled`, which is exactly what the Tag used to restate.
 *
 * ONE THING REMAINS THE TAG'S, plus a margin. The Tag used to override the focus ring too,
 * because `Button.recipe` drew a single outline where the file draws the library's two-band
 * "Focused" style. #1180 adopted that ring into the Button from the same effect tokens, so the
 * override is gone and the ring now comes from the control itself — which is where it belonged.
 *
 * What is left is **the 24px touch target**: WCAG 2.5.8 wants 24px and the file draws 16, so a
 * centred pseudo-element grows the hit area without touching layout or paint. (`Button.recipe`'s
 * own scale note carries the same warning against `3xs` on anything touch-reachable.)
 */
export const tagRemoveRecipe = cva([
  // The file's `Icon Container`: 2px between the label's own 2px and the control. Modelled as a
  // margin here rather than as a wrapper frame, because an empty frame whose only job is padding
  // is a Figma construct, not a DOM one.
  'ml-0.5 shrink-0',

  // The 24px target. A child of this button, so it cannot overlap the label's own hit area at
  // `med`; at `sm` it reaches into the 4px gap, and being later in paint order it wins there — a
  // small overlap is a better failure than a target nobody can hit. Since `sm` grew to 24px the
  // target no longer overflows the tag vertically at either size.
  'relative after:absolute after:top-1/2 after:left-1/2 after:size-6 after:-translate-x-1/2',
  'after:-translate-y-1/2 after:content-[""]',
]);

/**
 * The × glyph.
 *
 * `size-full` rather than `size-3`: `IconButton` wraps it in `buttonIconRecipe`, a 12px box at
 * `3xs`, so the glyph fills whatever the BUTTON's size scale says rather than pinning a number the
 * scale could move away from.
 */
export const tagRemoveIconRecipe = cva(['size-full']);
